import { type FC, useRef, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import ReCAPTCHA from 'react-google-recaptcha'
import { debounce } from 'lodash'
import { GOOGLE_RECAPTCHA_SITE_KEY_V2 } from 'shared/config'
import { type ReCaptchaV2Store } from 'entities/Auth/ui/ReCaptcha/ui/ReCaptchaV2'

type IReCaptchaProps = {
  store: ReCaptchaV2Store
}

export const ReCaptchaV2: FC<IReCaptchaProps> = observer(({ store }) => {
  const [isPassed, setIsPassed] = useState(false)

  const debounceIsPassed = debounce((callback: () => void) => {
    callback()
  }, 1000)

  const ref = useRef<ReCAPTCHA>(null)
  const { setToken, setApi } = store

  useEffect(() => {
    if (!ref.current) return

    setApi(ref.current)
  }, [])

  useEffect(() => {
    if (!ref.current) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.target instanceof HTMLDivElement &&
          mutation.target.querySelector('iframe[src*="recaptcha/api2"]')
        ) {
          debounceIsPassed(() => {
            if (isPassed) {
              setIsPassed(false)
              return
            }

            const element = mutation.target

            if (element instanceof HTMLDivElement && element.style.visibility === 'hidden') {
              if (!ref.current) return
              ref.current.executionReject?.(new Error('re_captcha_v2_hidden'))
            }
          })
        }
      })
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    })

    return () => {
      observer.disconnect()
    }
  }, [isPassed])

  return (
    <ReCAPTCHA
      ref={ref}
      sitekey={GOOGLE_RECAPTCHA_SITE_KEY_V2}
      onChange={(token) => {
        if (!token) return

        setIsPassed(true)
        setToken(token)
      }}
      onExpired={() => {
        setToken('')
      }}
      size={'invisible'}
    />
  )
})
